// src/mobile_pages/quality/components/MobileQualityMIView.jsx
// 모바일 전용 품질 자재검사 뷰 (테이블 대신 카드 형식)

import React from "react";
import { Calendar, Package, Building2, CheckCircle, FileText, Hash } from "lucide-react";

export default function MobileQualityMIView({ data }) {
    if (!data) return null;

    const header = data.header || {};
    const items = data.items || [];

    const getResultStyle = (result) => {
        const r = (result || "").toLowerCase();
        if (r.includes("합격") || r.includes("pass") || r.includes("적합")) {
            return "bg-emerald-50 text-emerald-700 border-emerald-200";
        }
        if (r.includes("불합격") || r.includes("fail") || r.includes("부적합")) {
            return "bg-red-50 text-red-700 border-red-200";
        }
        return "bg-slate-50 text-slate-600 border-slate-200";
    };

    return (
        <div className="space-y-4">

            {/* 기본 정보 카드 */}
            <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
                <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                    <h3 className="text-sm font-semibold text-slate-700">기본 정보</h3>
                </div>
                <div className="p-4 space-y-3">
                    {/* 제목 */}
                    <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                            <FileText size={16} className="text-blue-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-xs text-slate-400 mb-0.5">제목</p>
                            <p className="text-sm text-slate-900 font-medium break-words">
                                {header.title || "-"}
                            </p>
                        </div>
                    </div>

                    {/* 검사일 */}
                    <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
                            <Calendar size={16} className="text-amber-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-xs text-slate-400 mb-0.5">검사일</p>
                            <p className="text-sm text-slate-900 font-medium">
                                {header.doc_date || "-"}
                            </p>
                        </div>
                    </div>

                    {/* 공급업체 */}
                    <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
                            <Building2 size={16} className="text-purple-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-xs text-slate-400 mb-0.5">공급업체</p>
                            <p className="text-sm text-slate-900 font-medium">
                                {header.supplier || "-"}
                            </p>
                        </div>
                    </div>

                    {/* 자재명 */}
                    <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                            <Package size={16} className="text-emerald-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-xs text-slate-400 mb-0.5">자재명</p>
                            <p className="text-sm text-slate-900 font-medium break-words">
                                {header.material_name || "-"}
                            </p>
                        </div>
                    </div>

                    {/* 규격 / LOT */}
                    <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-100">
                        <div>
                            <p className="text-xs text-slate-400 mb-0.5">규격</p>
                            <p className="text-sm text-slate-700">{header.spec || "-"}</p>
                        </div>
                        <div>
                            <p className="text-xs text-slate-400 mb-0.5">LOT</p>
                            <p className="text-sm text-slate-700">{header.lot_no || "-"}</p>
                        </div>
                    </div>

                    {/* 수량 / 단위 */}
                    <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-100">
                        <div>
                            <p className="text-xs text-slate-400 mb-0.5">수량</p>
                            <p className="text-sm text-slate-700">{header.quantity || "-"}</p>
                        </div>
                        <div>
                            <p className="text-xs text-slate-400 mb-0.5">단위</p>
                            <p className="text-sm text-slate-700">{header.unit || "-"}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* 검사 항목 */}
            <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
                <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-slate-700">검사 항목</h3>
                    <span className="text-xs text-slate-400">{items.length}개</span>
                </div>

                {items.length === 0 ? (
                    <div className="p-6 text-center">
                        <p className="text-sm text-slate-400">검사 항목이 없습니다.</p>
                    </div>
                ) : (
                    <div className="divide-y divide-slate-100">
                        {items.map((row, idx) => (
                            <div key={idx} className="p-4">
                                <div className="flex items-start justify-between gap-3 mb-2">
                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                        <CheckCircle size={16} className="text-slate-400 flex-shrink-0" />
                                        <span className="text-sm font-medium text-slate-900 break-words">
                                            {row.item_name || `항목 ${idx + 1}`}
                                        </span>
                                    </div>
                                    <span className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full border flex-shrink-0 ${getResultStyle(row.result_label)}`}>
                                        {row.result_label || "-"}
                                    </span>
                                </div>
                                {row.criteria && (
                                    <div className="ml-6 mb-1">
                                        <span className="text-xs text-slate-400">기준: </span>
                                        <span className="text-xs text-slate-600">{row.criteria}</span>
                                    </div>
                                )}
                                {row.remark && (
                                    <div className="ml-6">
                                        <span className="text-xs text-slate-400">비고: </span>
                                        <span className="text-xs text-slate-600">{row.remark}</span>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
